import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { MapView } from './MapView';
// v17: import the logo through Vite so the generated URL honors the
// `base: './'` config. Assets imported from `src/` get hashed and
// referenced RELATIVE to index.html; this is the only path scheme that
// survives Cloudflare Pages nested-prefix / proxied deployments. The
// previous v16 build used a brittle absolute "/assets/..." path that
// 404'd whenever the site was served under a non-root prefix — that was
// the root cause of the missing watermark in the production screenshot.
import deliverfundLogoUrl from './assets/deliverfund-logo.png';
import { fetchDay, fetchDays, fetchManifest, fetchPhone } from './api';
import type { DayDot, ISODate, PhoneHistoryEntry } from './types';
import {
  formatRangeLabel,
  formatShortDate,
  intervalForSpeed,
  nextSpeed,
  PLAYBACK_SPEED_DEFAULT,
  progressFraction,
  type PlaybackSpeed,
  type PlaybackStatus,
} from './playback';

type LoadState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; data: T }
  | { status: 'error'; error: string };

// Playback experience:
//   1. /api/days on load. Select days[0] (earliest date).
//   2. Fetch /api/day/days[0]. Once the first day's dots are rendered, autoplay begins.
//   3. Every PLAYBACK_INTERVAL_MS the index advances. We clear old markers and
//      render the next day. Viewport center/zoom are preserved by design.
//   4. When the final date is reached, playback stops on complete state and the
//      Restart control becomes the primary action.
//   5. Date is never user-selectable from a dropdown; the current date is only
//      shown as a read-only overlay and is driven by play/pause/restart.
export function App() {
  const [days, setDays] = useState<LoadState<ISODate[]>>({ status: 'idle' });
  const [index, setIndex] = useState<number>(0);
  const [playbackStatus, setPlaybackStatus] = useState<PlaybackStatus>('idle');

  // Cache day -> dots so scrubbing back via Restart does not re-fetch every frame.
  const dotsCacheRef = useRef<Map<ISODate, DayDot[]>>(new Map());
  const [currentDots, setCurrentDots] = useState<DayDot[] | null>(null);
  // v7: dayLoading state removed. The playback bar no longer shows a
  // "loading" label; cached day-fetches are silent to the user.
  const [dayError, setDayError] = useState<string | null>(null);

  const [selectedPhoneId, setSelectedPhoneId] = useState<number | null>(null);
  const [phoneHistory, setPhoneHistory] = useState<LoadState<PhoneHistoryEntry[]>>({ status: 'idle' });

  // ---- view controls -----------------------------------------------------
  // v6 defaults:
  //   - `showColoredDots` defaults to ON — colored filled markers are the
  //     primary playback view.
  //   - `showCentralOpen` defaults to OFF — neutral hollow central-
  //     logistics rings are opt-in so first impression is a clean colored
  //     cohort rather than a mixed-symbol map.
  // Both toggles are independent: either may be on or off, giving four
  // combinations including "all off" which yields an empty dot layer with
  // controls still fully usable.
  // `originStateFilter` filters by a phone's LOCKED origin_state
  // (first-plotted-primary lower-48 state), not by its current physical
  // location. Empty string = All states.
  const [showColoredDots, setShowColoredDots] = useState<boolean>(true);
  const [showCentralOpen, setShowCentralOpen] = useState<boolean>(false);
  const [originStateFilter, setOriginStateFilter] = useState<string>('');

  // Intro modal: shown once per page load. React state only — no
  // localStorage/sessionStorage/cookies — so the explanation is present
  // on every fresh entry for this public educational context.
  const [showIntro, setShowIntro] = useState<boolean>(true);

  // v12: playback speed multiplier. 1× = base interval (500 ms/day);
  // 2× halves the interval for a fast fly-through. Cycled via a compact
  // pill in the playback bar.
  const [playbackSpeed, setPlaybackSpeed] = useState<PlaybackSpeed>(PLAYBACK_SPEED_DEFAULT);

  // List of states to offer in the dropdown. Populated from the manifest
  // (build-static-data.mjs emits `lower_48_states`); falls back to a
  // hardcoded list if the manifest is ever missing that field.
  const [stateOptions, setStateOptions] = useState<string[]>([]);

  // ---- days load ---------------------------------------------------------
  const loadDays = useCallback(async () => {
    setDays({ status: 'loading' });
    try {
      const list = await fetchDays();
      setDays({ status: 'ok', data: list });
      setIndex(0);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setDays({ status: 'error', error: msg });
    }
  }, []);

  // ---- manifest-driven state options ------------------------------------
  // We fetch the manifest once on mount to populate the state-filter
  // dropdown. Failure is non-fatal: if the manifest fetch fails the map
  // still works, and the dropdown falls back to the 48-state allowlist.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await fetchManifest();
        if (cancelled) return;
        const list = Array.isArray(m.lower_48_states) && m.lower_48_states.length
          ? m.lower_48_states
          : FALLBACK_LOWER_48;
        setStateOptions(list);
      } catch {
        if (!cancelled) setStateOptions(FALLBACK_LOWER_48);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    loadDays();
  }, [loadDays]);

  const daysList = days.status === 'ok' ? days.data : [];
  const total = daysList.length;
  const currentDate: ISODate | null = total > 0 ? daysList[Math.min(index, total - 1)] : null;

  // ---- fetch dots for the current date ----------------------------------
  useEffect(() => {
    if (!currentDate) return;
    const cache = dotsCacheRef.current;
    const cached = cache.get(currentDate);
    if (cached) {
      setCurrentDots(cached);
      setDayError(null);
      return;
    }
    let cancelled = false;
    setDayError(null);
    fetchDay(currentDate).then(
      (data) => {
        if (cancelled) return;
        cache.set(currentDate, data);
        setCurrentDots(data);
      },
      (err) => {
        if (cancelled) return;
        setDayError(err instanceof Error ? err.message : String(err));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [currentDate]);

  // ---- autoplay: start after days load + first day rendered -------------
  // When days resolve and the first day's dots are on the map, kick off playback.
  useEffect(() => {
    if (days.status !== 'ok' || total === 0) return;
    if (playbackStatus !== 'idle') return;
    if (!currentDots) return;
    if (index !== 0) return;
    setPlaybackStatus('playing');
  }, [days.status, total, playbackStatus, currentDots, index]);

  // Playback tick. Only runs in 'playing' state. Advances to the next date
  // or transitions to 'complete' at the end.
  useEffect(() => {
    if (playbackStatus !== 'playing') return;
    if (total === 0) return;
    // If at last index, mark complete.
    if (index >= total - 1) {
      setPlaybackStatus('complete');
      return;
    }
    const handle = window.setTimeout(() => {
      setIndex((i) => Math.min(i + 1, total - 1));
    }, intervalForSpeed(playbackSpeed));
    return () => window.clearTimeout(handle);
  }, [playbackStatus, index, total, playbackSpeed]);

  // ---- controls ---------------------------------------------------------
  const onPlayPause = useCallback(() => {
    setPlaybackStatus((s) => {
      if (s === 'playing') return 'paused';
      if (s === 'paused' || s === 'idle') return 'playing';
      // complete: pressing play on complete does nothing; Restart handles that.
      return s;
    });
  }, []);

  const onRestart = useCallback(() => {
    setIndex(0);
    setPlaybackStatus('playing');
  }, []);

  // Manual scrub from the timeline slider. We pause playback while the
  // user is dragging so the tick effect does not race with their input.
  // Leaving playback in 'paused' after scrub lets them press Play to
  // resume from the new position — matches common video-scrubber UX.
  const onScrub = useCallback((nextIdx: number) => {
    if (total === 0) return;
    const clamped = Math.max(0, Math.min(total - 1, Math.floor(nextIdx)));
    setIndex(clamped);
    setPlaybackStatus((s) => (s === 'playing' ? 'paused' : s));
  }, [total]);

  // v21 a11y: PageUp/PageDown jump a week. Arrow keys, Home and End are left
  // to the native <input type="range"> (which already does ±1 step and
  // first/last) so we do not double-handle them.
  const onScrubKeyDown = useCallback((e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (total === 0) return;
    if (e.key !== 'PageUp' && e.key !== 'PageDown') return;
    e.preventDefault();
    const delta = e.key === 'PageUp' ? 7 : -7;
    onScrub(index + delta);
  }, [index, total, onScrub]);

  // ---- dot click -> phone history ---------------------------------------
  const onDotClick = useCallback(async (phoneId: number) => {
    setSelectedPhoneId(phoneId);
    setPhoneHistory({ status: 'loading' });
    try {
      const data = await fetchPhone(phoneId);
      setPhoneHistory({ status: 'ok', data });
    } catch (err) {
      setPhoneHistory({ status: 'error', error: err instanceof Error ? err.message : String(err) });
    }
  }, []);

  const onCycleSpeed = useCallback(() => {
    setPlaybackSpeed((s) => nextSpeed(s));
  }, []);

  const closePhonePanel = useCallback(() => {
    setSelectedPhoneId(null);
    setPhoneHistory({ status: 'idle' });
  }, []);

  // ---- derived display --------------------------------------------------
  const progress = useMemo(() => progressFraction(index, total), [index, total]);
  const historyData = phoneHistory.status === 'ok' ? phoneHistory.data : null;
  const rangeLabel = useMemo(() => formatRangeLabel(daysList), [daysList]);
  const dayNumber = total > 0 ? index + 1 : 0;

  const isPlaying = playbackStatus === 'playing';
  const isComplete = playbackStatus === 'complete';

  // ---- render -----------------------------------------------------------
  return (
    <div className="app">
      <main className="main">
        <MapView
          dots={currentDots}
          onDotClick={onDotClick}
          selectedPhoneId={selectedPhoneId}
          selectedHistory={historyData}
          showCentralOpen={showCentralOpen}
          showColoredDots={showColoredDots}
          originStateFilter={originStateFilter}
        />

        <MapControls
          showColoredDots={showColoredDots}
          onToggleColoredDots={() => setShowColoredDots((v) => !v)}
          showCentralOpen={showCentralOpen}
          onToggleCentralOpen={() => setShowCentralOpen((v) => !v)}
          originStateFilter={originStateFilter}
          onOriginStateFilterChange={setOriginStateFilter}
          stateOptions={stateOptions}
          selectedPhoneId={selectedPhoneId}
          onClearSelection={closePhonePanel}
        />

        {/* Playback control bar (bottom). Fully transparent, no card surface;
            a compact single-row strip: primary action + date readout + progress +
            range meta. One restart control serves both mid-run and complete states. */}
        <div
          className="playback-bar"
          data-testid="panel-playback"
          role="region"
          aria-label="Playback controls"
          data-state={playbackStatus}
        >
          <div className="playback-bar-inner">
            <button
              type="button"
              className="pb-btn pb-btn-primary"
              data-testid={isComplete ? 'button-restart' : 'button-play-pause'}
              onClick={isComplete ? onRestart : onPlayPause}
              aria-label={
                isComplete
                  ? 'Restart playback'
                  : isPlaying
                  ? 'Pause playback'
                  : 'Play playback'
              }
              disabled={total === 0}
            >
              {isComplete ? (
                <IconRestart />
              ) : isPlaying ? (
                <IconPause />
              ) : (
                <IconPlay />
              )}
              <span className="pb-btn-label">
                {isComplete ? 'Restart' : isPlaying ? 'Pause' : 'Play'}
              </span>
            </button>

            {/* v10: always render the restart-icon so the 6-column grid stays
                stable. When the run is complete the main Play button becomes
                Restart, so this duplicate is hidden to avoid two identical
                controls — but kept in the DOM so the slider's 1fr column
                does not shift left and collapse its visual width.
                Restart-at-end fix: while complete this placeholder is fully
                inert (hidden, disabled, aria-hidden, untabbable, no pointer
                events) AND carries no "Restart" name/title, so the only
                control exposed as Restart at Day N of N is the visible,
                working primary button. Previously the hidden icon kept its
                "Restart from first day" label, so anything that targeted it
                by name at the end clicked a dead control. */}
            <button
              type="button"
              className="pb-btn pb-btn-ghost pb-btn-icon"
              data-testid="button-restart-always"
              onClick={onRestart}
              disabled={total === 0 || index === 0 || isComplete}
              aria-label={isComplete ? undefined : 'Restart from first day'}
              title={isComplete ? undefined : 'Restart from first day'}
              style={isComplete ? { visibility: 'hidden', pointerEvents: 'none' } : undefined}
              aria-hidden={isComplete || undefined}
              tabIndex={isComplete ? -1 : 0}
            >
              <IconRestart />
            </button>

            <div className="pb-readout">
              <span className="pb-date" data-testid="text-current-date">
                {currentDate ? formatShortDate(currentDate) : '—'}
              </span>
              <span className="pb-date-iso" data-testid="text-current-date-iso">
                {currentDate ?? ''}
              </span>
            </div>

            {/* Draggable timeline slider. Replaces the visual-only
                progress bar. Uses a native <input type="range"> for
                built-in keyboard, mouse, and touch support; an
                absolutely positioned fill div traces the value so the
                visual weight matches the old bar. Scrubbing pauses
                playback; pressing Play resumes from the scrub point. */}
            <div
              className="pb-slider"
              data-testid="slider-playback"
              data-state={playbackStatus}
            >
              <div className="pb-slider-track">
                <div
                  className="pb-slider-fill"
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
              {/* v21 a11y: a native range already gives us Tab focus,
                  ArrowLeft/Right/Up/Down (±1 day), and Home/End (first/last
                  day) for free, and `onChange` fires for all of them — so
                  keyboard and mouse drag share one code path. The redundant
                  aria-valuemin/max/now were removed: the native element
                  exposes those from min/max/value, and the hand-written
                  aria-valuenow could drift from the clamped `value`.
                  aria-valuetext is kept so screen readers announce the date
                  ("2026-01-07") instead of a bare index ("4"). */}
              <input
                type="range"
                className="pb-slider-input"
                min={0}
                max={Math.max(0, total - 1)}
                step={1}
                value={Math.min(index, Math.max(0, total - 1))}
                disabled={total === 0}
                aria-label="Playback timeline"
                aria-valuetext={
                  currentDate
                    ? `${formatShortDate(currentDate)}, day ${dayNumber} of ${total}`
                    : undefined
                }
                data-testid="input-playback-scrub"
                onChange={(e) => onScrub(Number(e.target.value))}
                onKeyDown={onScrubKeyDown}
              />
            </div>

            <div className="pb-meta">
              <span className="pb-count" data-testid="text-day-index">
                {total > 0 ? `Day ${dayNumber} of ${total}` : ''}
                {dayError ? ' · error' : ''}
              </span>
              <span className="pb-range" data-testid="text-range">
                {rangeLabel}
              </span>
            </div>

            <span className="pb-status" data-testid="text-playback-status" aria-live="polite">
              {playbackStatus === 'playing' && 'playing'}
              {playbackStatus === 'paused' && 'paused'}
              {playbackStatus === 'complete' && 'complete'}
            </span>

            {/* v12: speed pill. Cycles through 1× and 2× on click.
                Compact, lives in column 7 of the playback grid so the
                slider stays pinned to its 1fr column (col 4). */}
            <button
              type="button"
              className="pb-btn pb-btn-ghost pb-speed"
              data-testid="button-speed"
              data-speed={playbackSpeed}
              onClick={onCycleSpeed}
              disabled={total === 0}
              aria-label={`Playback speed ${playbackSpeed}×. Click to change.`}
              title={`Playback speed: ${playbackSpeed}×`}
            >
              <span className="pb-btn-label">{playbackSpeed}×</span>
            </button>
          </div>

          {days.status === 'error' && (
            <div className="pb-error" data-testid="text-error-days">
              Timeline load failed: {days.error}{' '}
              <button type="button" onClick={loadDays} data-testid="button-retry-days">Retry</button>
            </div>
          )}
          {dayError && (
            <div className="pb-error" data-testid="text-error-dots">
              Day load failed: {dayError}
            </div>
          )}
        </div>

        {selectedPhoneId !== null && (
          <PhonePanel
            phoneId={selectedPhoneId}
            state={phoneHistory}
            onClose={closePhonePanel}
            onRetry={() => onDotClick(selectedPhoneId)}
          />
        )}

        {showIntro && <IntroModal onClose={() => setShowIntro(false)} />}

        {/* v17: DeliverFund watermark. Rendered as a direct child of
         * .main (sibling of .playback-bar) with z-index 1200 so it
         * stacks ABOVE the playback bar (z-index 500) and ABOVE Leaflet
         * controls (z-index 1000). The watermark is bottom-right with an
         * 80px bottom inset that clears the playback-bar pill on desktop.
         * Mobile breakpoint below narrows and lowers it. `pointer-events:
         * none` + `aria-hidden="true"` so it never steals drags, dot
         * clicks, or screen-reader focus. */}
        <div
          className="map-watermark"
          data-testid="map-watermark"
          aria-hidden="true"
        >
          <img className="watermark-img" src={deliverfundLogoUrl} alt="" />
        </div>

        {/* v20: lower-right full-color logo mark. Unfiltered so the
         * red accent remains visible. Separate from the faint centered
         * screenshot watermark above. pointer-events: none + aria-hidden
         * so it can never block controls or steal focus. */}
        <div
          className="map-logo-mark"
          data-testid="map-logo-mark"
          aria-hidden="true"
        >
          <img className="logo-mark-img" src={deliverfundLogoUrl} alt="" />
        </div>
      </main>
    </div>
  );
}

// Inline SVG icons for the playback bar. Tiny, monochromatic, currentColor so
// they inherit foreground color for hover/disabled states.
function IconPlay() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
      <path d="M3.5 2.25 11.5 7l-8 4.75V2.25Z" fill="currentColor" />
    </svg>
  );
}
function IconPause() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
      <rect x="3" y="2.5" width="2.6" height="9" rx="0.6" fill="currentColor" />
      <rect x="8.4" y="2.5" width="2.6" height="9" rx="0.6" fill="currentColor" />
    </svg>
  );
}
function IconRestart() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
      <path
        d="M7 2.5a4.5 4.5 0 1 1-4.47 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path d="M7 0.5v3.2L4.2 2.1 7 .5Z" fill="currentColor" />
    </svg>
  );
}

function PhonePanel({
  phoneId,
  state,
  onClose,
  onRetry,
}: {
  phoneId: number;
  state: LoadState<PhoneHistoryEntry[]>;
  onClose: () => void;
  onRetry: () => void;
}) {
  return (
    <div className="phone-panel" data-testid="panel-phone-detail">
      <header>
        <div>
          <div className="title">Phone #{phoneId}</div>
          <div className="sub">Read-only sighting history</div>
        </div>
        <button data-testid="button-close-phone" onClick={onClose} aria-label="Close detail panel">Close</button>
      </header>
      <div className="body">
        <PhoneBody state={state} onRetry={onRetry} />
      </div>
    </div>
  );
}

function PhoneBody({ state, onRetry }: { state: LoadState<PhoneHistoryEntry[]>; onRetry: () => void }) {
  if (state.status === 'loading' || state.status === 'idle') {
    return <div className="loading-inline" data-testid="text-loading-phone">…</div>;
  }
  if (state.status === 'error') {
    return (
      <div className="banner" data-testid="text-error-phone">
        <div className="msg">History failed: {state.error}</div>
        <button data-testid="button-retry-phone" onClick={onRetry}>Retry</button>
      </div>
    );
  }
  const entries = state.data;
  if (entries.length === 0) {
    return <div className="muted small">No sightings for this phone.</div>;
  }

  const first = entries[0];
  const last = entries[entries.length - 1];
  return (
    <>
      <div className="small muted" style={{ marginBottom: 8 }}>
        <div>
          Origin: <span style={{ color: 'var(--text)' }}>{first.origin_state ?? '—'}</span>
          {' · '}
          <span className="badge" style={{ borderColor: first.color, color: first.color }}>★ {first.star}</span>
          {first.logistics_flag ? ' ' : null}
          {first.logistics_flag ? <span className="badge logistics">logistics</span> : null}
        </div>
        <div>
          {entries.length} sightings · {first.date} to {last.date}
        </div>
      </div>
      <ul className="timeline" data-testid="list-phone-timeline">
        {entries.map((h, i) => (
          <li key={`${h.date}-${i}`}>
            <span className="dot" style={{ background: h.color }} />
            <span className="date">{h.date}</span>
            <span className="where">
              {h.city || '—'}<span className="state-name">, {h.state}</span>
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

// Fallback list of lower-48 states plus District of Columbia. Used only
// if manifest.json is unreachable or missing `lower_48_states`. Must
// stay in sync with scripts/build-static-data.mjs LOWER_48 allowlist.
const FALLBACK_LOWER_48: string[] = [
  'Alabama', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'District of Columbia',
  'Florida', 'Georgia', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts',
  'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska',
  'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming',
];

/**
 * Compact map overlay: a translucent glass pill in the top-right of the map.
 * Exposes (v6):
 *   - "State" dropdown: All states (default) or one of the 48 lower-48
 *     states + DC. Filters by each phone's LOCKED origin_state.
 *   - "Colored dots" toggle: shows/hides the filled colored markers for
 *     non-central phones. Default ON.
 *   - "Open circles" toggle: shows/hides the neutral hollow central-
 *     logistics rings. Default OFF (v6 change from v5).
 *   - "Clear path" button: only shown when a phone's history track is
 *     active.
 *
 * The control sits on top of the map as a single horizontally-oriented
 * strip so the map area stays maximal; no sidebar.
 */
function MapControls({
  showColoredDots,
  onToggleColoredDots,
  showCentralOpen,
  onToggleCentralOpen,
  originStateFilter,
  onOriginStateFilterChange,
  stateOptions,
  selectedPhoneId,
  onClearSelection,
}: {
  showColoredDots: boolean;
  onToggleColoredDots: () => void;
  showCentralOpen: boolean;
  onToggleCentralOpen: () => void;
  originStateFilter: string;
  onOriginStateFilterChange: (v: string) => void;
  stateOptions: string[];
  selectedPhoneId: number | null;
  onClearSelection: () => void;
}) {
  return (
    <div className="map-controls" data-testid="panel-map-controls" role="region" aria-label="Map view controls">
      <label className="mc-field">
        <span className="mc-label">State</span>
        <select
          data-testid="select-state-filter"
          className="mc-select"
          value={originStateFilter}
          onChange={(e) => onOriginStateFilterChange(e.target.value)}
          aria-label="Filter by locked origin state"
          title="Filter phones by their locked first-plotted origin state. Phones still move across the USA over the timeline."
        >
          <option value="">All states</option>
          {stateOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </label>

      <button
        type="button"
        className={`mc-toggle ${showColoredDots ? 'is-on' : 'is-off'}`}
        data-testid="button-toggle-colored-dots"
        role="switch"
        aria-checked={showColoredDots}
        onClick={onToggleColoredDots}
        title={showColoredDots ? 'Hide trafficker signals' : 'Show trafficker signals'}
      >
        <span className="mc-toggle-dot mc-toggle-dot-filled" aria-hidden="true" />
        <span className="mc-toggle-label">Trafficker signals</span>
      </button>

      <button
        type="button"
        className={`mc-toggle ${showCentralOpen ? 'is-on' : 'is-off'}`}
        data-testid="button-toggle-open-circles"
        role="switch"
        aria-checked={showCentralOpen}
        onClick={onToggleCentralOpen}
        title={showCentralOpen ? 'Hide logistics centers' : 'Show logistics centers'}
      >
        <span className="mc-toggle-dot mc-toggle-dot-hollow" aria-hidden="true" />
        <span className="mc-toggle-label">Logistics Centers</span>
      </button>

      {selectedPhoneId !== null && (
        <button
          type="button"
          className="mc-clear"
          data-testid="button-clear-selection"
          onClick={onClearSelection}
          aria-label="Clear selected phone path"
          title="Clear selected phone path"
        >
          Clear path
        </button>
      )}
    </div>
  );
}

/**
 * First-visit explainer modal. Shown once per page load (React state
 * only — no localStorage/sessionStorage/cookies). Closes via:
 *   - the primary "View the map" button
 *   - the `×` close button
 *   - Escape key
 *   - backdrop click (mousedown originating on the overlay)
 *
 * role=dialog + aria-modal + aria-labelledby on the title. The primary
 * CTA is focused on mount so keyboard users land on the expected action.
 */
function IntroModal({ onClose }: { onClose: () => void }) {
  const ctaRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const prevActive = document.activeElement as HTMLElement | null;
    ctaRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (prevActive && typeof prevActive.focus === 'function') {
        prevActive.focus();
      }
    };
  }, [onClose]);

  const onOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      className="intro-overlay"
      data-testid="intro-modal-overlay"
      onMouseDown={onOverlayMouseDown}
    >
      <div
        ref={dialogRef}
        className="intro-modal"
        data-testid="intro-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="intro-modal-title"
        aria-describedby="intro-modal-body"
      >
        <button
          type="button"
          className="intro-close"
          data-testid="intro-modal-close"
          aria-label="Close introduction"
          onClick={onClose}
        >
          ×
        </button>
        <h2 id="intro-modal-title" className="intro-title">How to read this map</h2>
        <div id="intro-modal-body" className="intro-body">
          <p>
            This map shows trafficking-related phone movement signals from Q1 2026.
            Each colored dot represents one unique phone number observed in the
            dataset. A phone receives its color from the state where it first
            appears, and that color stays with the phone as it moves across the
            country.
          </p>
          <p>
            The story is in the movement: a cluster that starts in one state can
            be watched as those same colored dots appear in other states over
            time. Use Play/Pause or drag the timeline to move through the
            quarter. Use the state filter to isolate phones that first appeared
            in a specific state.
          </p>
          <p>
            Hollow circles mark phones that appear in multiple locations on the
            same day, a central-logistics signal. They are hidden by default and
            can be turned on from the map controls. Click any dot to trace that
            single phone&rsquo;s path.
          </p>
        </div>
        <div className="intro-actions">
          <button
            ref={ctaRef}
            type="button"
            className="intro-cta"
            data-testid="intro-modal-cta"
            onClick={onClose}
          >
            View the map
          </button>
        </div>
      </div>
    </div>
  );
}
